(() => {
  if (customElements.get('pg-cardlist')) return;

  class PgCardlist extends HTMLElement {
    connectedCallback() {
      if (this.dataset.enhanced === 'true') return;
      this.dataset.enhanced = 'true';

      this.cards = Array.from(this.querySelectorAll('[data-pg-card]'));
      this.searchInput = this.querySelector('[data-pg-search]');
      this.clearSearchButton = this.querySelector('[data-pg-clear-search]');
      this.facetButtons = Array.from(this.querySelectorAll('[data-pg-facet]'));
      this.resetButton = this.querySelector('[data-pg-reset]');
      this.resultCount = this.querySelector('[data-pg-result-count]');
      this.noResults = this.querySelector('[data-pg-no-results]');
      this.tools = this.querySelector('[data-pg-tools]');
      this.filterToggle = this.querySelector('[data-pg-filter-toggle]');
      this.dialog = this.querySelector('[data-pg-dialog]');
      this.dialogImage = this.querySelector('[data-pg-dialog-image]');
      this.dialogTitle = this.querySelector('[data-pg-dialog-title]');
      this.dialogNumber = this.querySelector('[data-pg-dialog-number]');
      this.dialogProduct = this.querySelector('[data-pg-dialog-product]');
      this.dialogClose = this.querySelector('[data-pg-dialog-close]');
      this.backToTop = this.querySelector('[data-pg-back-top]');
      this.topHeading = this.querySelector('[data-pg-top-heading]');
      this.activeFacet = 'all';
      this.cardlistCode = this.normalize(this.dataset.cardlistCode);
      this.lastTrigger = null;
      this.scrollLock = null;

      if (!this.cards.length) return;

      this.prepareFacetButtons();
      this.bindFilters();
      this.bindDialog();
      this.bindBackToTop();
      this.applyFilters();
    }

    disconnectedCallback() {
      this.unlockPage();
    }

    normalize(value) {
      return String(value || '')
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .toLocaleLowerCase()
        .trim();
    }

    prepareFacetButtons() {
      this.facetButtons.forEach((button) => {
        const facet = this.normalize(button.dataset.pgFacet);
        if (!facet || facet === 'all') return;
        const available = this.cards.some((card) => this.matchesFacet(card, facet));
        button.hidden = !available;
        button.disabled = !available;
      });
    }

    normalizeCategory(value) {
      const category = this.normalize(value);
      if (category === 'pokemon') return 'pokemon';
      if (['trainer', 'dresseur', 'entrenador', 'allenatore'].includes(category)) return 'trainer';
      if (['energy', 'energie', 'energia'].includes(category)) return 'energy';
      if (['stadium', 'stade', 'estadio', 'stadio', 'stadion'].includes(category)) return 'stadium';
      return category;
    }

    getCollectorNumber(card) {
      const match = String(card.dataset.number || '').match(/^\s*0*(\d+)/);
      return match ? Number.parseInt(match[1], 10) : 0;
    }

    getExplicitFacets(card) {
      return new Set(
        String(card.dataset.filters || '')
          .split(/[\s,|]+/)
          .map((value) => this.normalize(value).replaceAll('_', '-'))
          .filter(Boolean),
      );
    }

    matchesFacet(card, rawFacet) {
      const facet = this.normalize(rawFacet).replaceAll('_', '-');
      if (!facet || facet === 'all') return true;

      const explicitFacets = this.getExplicitFacets(card);
      if (explicitFacets.has(facet)) return true;
      if (explicitFacets.size) return false;

      const category = this.normalizeCategory(card.dataset.category);
      const rarity = this.normalize(card.dataset.rarity);
      const name = this.normalize(card.dataset.name);
      const collectorNumber = this.getCollectorNumber(card);
      const isBaseCard = card.dataset.series === 'base';
      const isMegaEx = isBaseCard && /(^|\s)mega\s/.test(name) && /\bex\b/.test(name);
      const isRegularEx = isBaseCard && /\bex\b/.test(name) && !isMegaEx;
      const isM6Stadium = this.cardlistCode === 'm6' && isBaseCard && collectorNumber >= 71 && collectorNumber <= 76;

      if (facet === 'mega-ex') return isMegaEx;
      if (facet === 'ex') return isRegularEx;
      if (facet === 'pokemon') return category === 'pokemon';
      if (facet === 'trainer') return category === 'trainer' || category === 'stadium';
      if (facet === 'stadium') return category === 'stadium' || isM6Stadium;
      if (facet === 'energy') return category === 'energy';
      if (['ar', 'sr', 'sar', 'mur'].includes(facet)) return rarity === facet;
      return false;
    }

    bindFilters() {
      this.searchInput?.addEventListener('input', () => this.applyFilters());

      this.clearSearchButton?.addEventListener('click', () => {
        this.searchInput.value = '';
        this.searchInput.focus();
        this.applyFilters();
      });

      this.facetButtons.forEach((button) => {
        button.addEventListener('click', () => {
          this.activeFacet = button.dataset.pgFacet || 'all';
          this.facetButtons.forEach((facetButton) => {
            facetButton.setAttribute('aria-pressed', String(facetButton === button));
          });
          this.applyFilters();
        });
      });

      this.resetButton?.addEventListener('click', () => {
        this.activeFacet = 'all';
        this.facetButtons.forEach((button) => {
          button.setAttribute('aria-pressed', String(button.dataset.pgFacet === 'all'));
        });
        if (this.searchInput) this.searchInput.value = '';
        this.applyFilters();
        this.searchInput?.focus();
      });

      this.filterToggle?.addEventListener('click', () => {
        const willCollapse = !this.tools.classList.contains('is-collapsed');
        this.tools.classList.toggle('is-collapsed', willCollapse);
        this.filterToggle.setAttribute('aria-expanded', String(!willCollapse));
      });
    }

    applyFilters() {
      const query = this.normalize(this.searchInput?.value);
      let visibleCount = 0;

      this.cards.forEach((card) => {
        const matchesQuery = !query || this.normalize(card.dataset.search).includes(query);
        const matchesActiveFacet = this.matchesFacet(card, this.activeFacet);
        const visible = matchesQuery && matchesActiveFacet;

        card.hidden = !visible;
        if (visible) visibleCount += 1;
      });

      if (this.clearSearchButton) this.clearSearchButton.hidden = !this.searchInput?.value;
      if (this.noResults) this.noResults.hidden = visibleCount !== 0;

      if (this.resultCount) {
        const template = this.resultCount.dataset.countTemplate || '[count]';
        this.resultCount.textContent = template.replace('[count]', String(visibleCount));
      }
    }

    bindDialog() {
      if (!this.dialog) return;

      this.querySelectorAll('[data-pg-card-trigger]').forEach((button) => {
        button.addEventListener('click', () => this.openDialog(button));
      });

      this.dialogClose?.addEventListener('click', () => this.closeDialog());

      this.dialog.addEventListener('click', (event) => {
        if (event.target === this.dialog) this.closeDialog();
      });

      this.dialog.addEventListener('close', () => {
        this.unlockPage();
        const trigger = this.lastTrigger;
        this.lastTrigger = null;
        trigger?.focus({ preventScroll: true });
      });

      this.dialog.addEventListener('cancel', () => {
        // The native dialog closes on Escape; cleanup runs in the close event.
      });
    }

    openDialog(trigger) {
      this.lastTrigger = trigger;
      this.dialogTitle.textContent = trigger.dataset.cardName || '';
      this.dialogNumber.textContent = trigger.dataset.cardNumber || '';

      const imageSource = trigger.dataset.imageSrc || '';
      if (imageSource) {
        this.dialogImage.src = imageSource;
        this.dialogImage.srcset = trigger.dataset.imageSrcset || '';
        this.dialogImage.sizes = '(min-width: 750px) 62vw, 92vw';
        this.dialogImage.alt = trigger.dataset.imageAlt || '';
        this.dialogImage.hidden = false;
      } else {
        this.dialogImage.hidden = true;
        this.dialogImage.removeAttribute('src');
        this.dialogImage.removeAttribute('srcset');
        this.dialogImage.alt = '';
      }

      const productUrl = trigger.dataset.productUrl || '';
      if (productUrl) {
        this.dialogProduct.href = productUrl;
        this.dialogProduct.hidden = false;
      } else {
        this.dialogProduct.hidden = true;
        this.dialogProduct.removeAttribute('href');
      }

      this.lockPage();
      if (typeof this.dialog.showModal === 'function') {
        this.dialog.showModal();
      } else {
        this.dialog.setAttribute('open', '');
        this.dialogClose?.focus();
      }
    }

    closeDialog() {
      if (!this.dialog) return;
      if (typeof this.dialog.close === 'function') {
        this.dialog.close();
      } else {
        this.dialog.removeAttribute('open');
        this.unlockPage();
        this.lastTrigger?.focus({ preventScroll: true });
        this.lastTrigger = null;
      }
    }

    lockPage() {
      if (this.scrollLock) return;

      const root = document.documentElement;
      const body = document.body;
      const scrollbarGap = Math.max(0, window.innerWidth - root.clientWidth);
      this.scrollLock = {
        rootOverflow: root.style.overflow,
        bodyOverflow: body.style.overflow,
        bodyPaddingRight: body.style.paddingRight,
      };

      root.style.overflow = 'hidden';
      body.style.overflow = 'hidden';
      if (scrollbarGap) body.style.paddingRight = `${scrollbarGap}px`;
    }

    unlockPage() {
      if (!this.scrollLock) return;

      document.documentElement.style.overflow = this.scrollLock.rootOverflow;
      document.body.style.overflow = this.scrollLock.bodyOverflow;
      document.body.style.paddingRight = this.scrollLock.bodyPaddingRight;
      this.scrollLock = null;
    }

    bindBackToTop() {
      this.backToTop?.addEventListener('click', (event) => {
        event.preventDefault();
        const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        this.scrollIntoView({ behavior: reducedMotion ? 'auto' : 'smooth', block: 'start' });
        window.setTimeout(() => this.topHeading?.focus({ preventScroll: true }), reducedMotion ? 0 : 350);
      });
    }
  }

  customElements.define('pg-cardlist', PgCardlist);
})();
